/**
 * Shared Preferences API
 *
 * Centralizes all customer preference operations (get, save, cache, events)
 * used across email-preferences-steps.js, interests.js, global.js,
 * and Liquid inline scripts.
 */

(function () {
  var ENDPOINT = '/apps/email_preferences';

  /**
   * Normalize a team handle: replace underscores with hyphens.
   */
  function normalizeTeamHandle(handle) {
    return handle ? handle.replace(/_/g, '-') : '';
  }

  /**
   * Parse a raw API response into a normalized preferences object.
   * Always returns { teams, types, styles, fits, silhouettes }.
   */
  function parsePreferencesResponse(data) {
    var teams = [];
    if (data.all_teams && data.all_teams.length > 0) {
      teams = data.all_teams;
    } else if (data.teams && data.teams.length > 0) {
      teams = data.teams;
    }
    teams = teams.map(normalizeTeamHandle);

    var styles = data.styles || [];
    var fits = styles
      .filter(function (s) { return s.startsWith('fit_'); })
      .map(function (s) { return s.replace('fit_', ''); });
    var silhouettes = styles
      .filter(function (s) { return s.startsWith('silhouettes_'); })
      .map(function (s) { return s.replace('silhouettes_', ''); });

    return {
      teams: teams,
      types: data.types || [],
      styles: styles,
      fits: fits,
      silhouettes: silhouettes,
    };
  }

  /**
   * GET preferences for a customer email.
   * Returns a promise resolving to the parsed preferences object.
   */
  function getPreferences(email) {
    return fetch(ENDPOINT + '?email=' + encodeURIComponent(email), {
      method: 'GET',
      headers: { accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        return parsePreferencesResponse(data);
      });
  }

  /**
   * Build the full league breakdown from a teams array.
   * Returns an object with mlb_teams, nfl_teams, etc.
   * Always deduplicates.
   */
  function buildLeagueTeams(teams) {
    var leagueTeams = {
      mlb_teams: [],
      nfl_teams: [],
      nba_teams: [],
      nhl_teams: [],
      wnba_teams: [],
      milb_teams: [],
      mls_teams: [],
      college_teams: [],
      motorsport_teams: [],
      international_soccer_teams: [],
    };

    if (window.teamsMapping && window.teamsMapping.populateLeagueTeams && teams.length > 0) {
      var populated = window.teamsMapping.populateLeagueTeams(teams);
      Object.keys(populated).forEach(function (key) {
        leagueTeams[key] = Array.from(new Set(populated[key]));
      });
    }

    return leagueTeams;
  }

  /**
   * Build the full save payload from preferences.
   *
   * @param {string} email
   * @param {Object} prefs - { teams: [], types?: [], fits?: [], silhouettes?: [] }
   * @returns {Object} - ready to JSON.stringify as POST body
   */
  function buildSavePayload(email, prefs) {
    var teams = (prefs.teams || []).map(normalizeTeamHandle);
    var uniqueTeams = Array.from(new Set(teams));
    var leagueTeams = buildLeagueTeams(uniqueTeams);

    var styles = [];
    if (prefs.fits) {
      prefs.fits.forEach(function (f) { styles.push('fit_' + f); });
    }
    if (prefs.silhouettes) {
      prefs.silhouettes.forEach(function (s) { styles.push('silhouettes_' + s); });
    }

    var properties = {
      all_teams: uniqueTeams,
      teams: uniqueTeams,
    };
    Object.assign(properties, leagueTeams);

    if (prefs.types && prefs.types.length > 0) {
      properties.types = prefs.types;
    }
    if (styles.length > 0) {
      properties.styles = styles;
    }

    return {
      email: email,
      properties: properties,
    };
  }

  /**
   * Clear all React Query / preferences caches from localStorage.
   */
  function clearPreferencesCache() {
    var keysToRemove = [
      'REACT_QUERY_OFFLINE_CACHE',
      'react-query-cache',
      'react-query',
    ];
    keysToRemove.forEach(function (key) { localStorage.removeItem(key); });

    Object.keys(localStorage).forEach(function (key) {
      if (
        key.startsWith('REACT_QUERY') ||
        key.startsWith('react-query') ||
        key.includes('tanstack') ||
        key.includes('preferences') ||
        key.includes('teams')
      ) {
        localStorage.removeItem(key);
      }
    });

    localStorage.setItem('CLEAR_REACT_QUERY_CACHE', 'true');
  }

  /**
   * After a successful save: update cookie, clear cache, notify listeners.
   */
  function notifyPreferencesUpdated(teams) {
    if (typeof window.gfSetTeamsCookie === 'function') {
      window.gfSetTeamsCookie((teams || []).map(normalizeTeamHandle));
    }
    clearPreferencesCache();
    window.dispatchEvent(new CustomEvent('preferences-updated'));
  }

  /**
   * Show the toast notification element.
   */
  function showToast() {
    var notification = document.querySelector('.preferences-toast');
    if (notification) {
      notification.classList.remove('animate');
      setTimeout(function () { notification.classList.add('animate'); }, 100);
      setTimeout(function () { notification.classList.remove('animate'); }, 3000);
    }
  }

  /**
   * POST preferences for a customer.
   *
   * @param {string} email
   * @param {Object} prefs - { teams: [], types?: [], fits?: [], silhouettes?: [] }
   * @returns {Promise}
   */
  function savePreferences(email, prefs) {
    var payload = buildSavePayload(email, prefs);
    var saved = false;

    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        saved = res.ok;
        return res.json();
      })
      .then(function (response) {
        showToast();
        notifyPreferencesUpdated(payload.properties.all_teams);
        if (saved && window.neLoyaltyActions) {
          window.neLoyaltyActions.recordChooseTeams(payload.properties.all_teams, email);
        }
        return response;
      });
  }

  window.preferencesAPI = {
    getPreferences: getPreferences,
    savePreferences: savePreferences,
    buildSavePayload: buildSavePayload,
    buildLeagueTeams: buildLeagueTeams,
    parsePreferencesResponse: parsePreferencesResponse,
    clearPreferencesCache: clearPreferencesCache,
    notifyPreferencesUpdated: notifyPreferencesUpdated,
    normalizeTeamHandle: normalizeTeamHandle,
    showToast: showToast,
  };
})();
